import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarIcon, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '~/lib/utils';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type WaitlistStatus = 'booked' | 'deleted' | 'expired' | 'notified' | 'open';
type WaitlistCallStatus = 'CALLED' | 'NOT_REACHED' | 'CONTACTED';

type WaitlistRow = {
    id: string;
    createdAt: string;
    email: string;
    storeName: string | null;
    bookableItemNames: string;
    desiredDate: string | null;
    desiredTimeOfDay: string | null;
    status: WaitlistStatus;
    customerId: number | null;
    customerName: string | null;
    customerPhone: string | null;
    callStatus: WaitlistCallStatus | null;
    callComment: string | null;
    hasExistingAppointment: boolean;
    existingAppointmentDate: string | null;
};

const callStatusLabels: Record<WaitlistCallStatus, string> = {
    CALLED: 'Angerufen',
    NOT_REACHED: 'Nicht erreicht',
    CONTACTED: 'Anderweitig kontaktiert',
};

function StatusBadge({ status }: { status: WaitlistStatus }) {
    switch (status) {
        case 'booked':
            return <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>Gebucht</Badge>;
        case 'deleted':
            return <Badge variant='outline' className='text-muted-foreground'>Gelöscht</Badge>;
        case 'expired':
            return <Badge variant='outline' className='bg-orange-50 text-orange-700 border-orange-200'>Abgelaufen</Badge>;
        case 'notified':
            return <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>Benachrichtigt</Badge>;
        case 'open':
            return <Badge variant='outline'>Offen</Badge>;
    }
}

const WaitlistSubmissions = () => {
    const navigate = useNavigate();
    const utils = trpc.useUtils();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const [page, setPage] = useState(0);
    const pageSize = 20;
    const [statusFilter, setStatusFilter] = useState<'all' | WaitlistStatus>('all');
    const [callStatusFilter, setCallStatusFilter] = useState<'all' | WaitlistCallStatus | 'NONE'>('all');
    const [storeFilter, setStoreFilter] = useState<string>('all');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [isSelectingToDate, setIsSelectingToDate] = useState(false);
    const [desiredDateSort, setDesiredDateSort] = useState<'asc' | 'desc' | undefined>(undefined);
    const [searchInput, setSearchInput] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');

    // Detail dialog state
    const [selectedRow, setSelectedRow] = useState<WaitlistRow | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editCallStatus, setEditCallStatus] = useState<WaitlistCallStatus | 'NONE'>('NONE');
    const [editCallComment, setEditCallComment] = useState('');

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(searchInput);
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    const { data, isLoading } = trpc.waitlistAdmin.getAll.useQuery({
        page,
        pageSize,
        status: statusFilter,
        storeId: storeFilter !== 'all' ? parseInt(storeFilter) : undefined,
        dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
        desiredDateSort,
        search: searchDebounced || undefined,
        callStatus: callStatusFilter,
    });

    const updateCallStatusMutation = trpc.waitlistAdmin.updateCallStatus.useMutation({
        onSuccess: () => {
            utils.waitlistAdmin.getAll.invalidate();
            setDialogOpen(false);
        },
    });

    const handleRowClick = (row: WaitlistRow) => {
        setSelectedRow(row);
        setEditCallStatus(row.callStatus ?? 'NONE');
        setEditCallComment(row.callComment ?? '');
        setDialogOpen(true);
    };

    const handleSave = () => {
        if (!selectedRow) return;
        updateCallStatusMutation.mutate({
            id: selectedRow.id,
            callStatus: editCallStatus === 'NONE' ? null : editCallStatus,
            callComment: editCallComment.trim() || null,
        });
    };

    const columns: ColumnDef<WaitlistRow>[] = [
        {
            accessorKey: 'createdAt',
            header: 'Erstellt',
            cell: ({ row }) => (
                <span className='text-xs'>
                    {format(new Date(row.original.createdAt), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => {
                const { email, customerId, customerName } = row.original;
                return (
                    <div className='max-w-[180px]'>
                        {customerName && customerId ? (
                            <div
                                className='font-medium cursor-pointer hover:underline text-primary truncate'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/customer/${customerId}`);
                                }}
                            >
                                {customerName}
                            </div>
                        ) : (
                            <div className='text-sm truncate'>{email}</div>
                        )}
                        {customerName && (
                            <div className='text-sm text-muted-foreground truncate'>{email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'customerPhone',
            header: 'Telefon',
            cell: ({ row }) => (
                <span className='text-sm'>{row.original.customerPhone ?? '-'}</span>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => (
                <span className='text-sm'>{row.original.storeName ?? '-'}</span>
            ),
        },
        {
            id: 'bookableItems',
            header: 'Leistungen',
            cell: ({ row }) => (
                <span className='text-sm block max-w-[250px] whitespace-normal break-words'>{row.original.bookableItemNames || '-'}</span>
            ),
        },
        {
            accessorKey: 'desiredDate',
            header: () => (
                <button
                    type='button'
                    className='inline-flex items-center gap-1 hover:text-foreground transition-colors'
                    onClick={() => {
                        setDesiredDateSort((prev) =>
                            prev === undefined ? 'asc' : prev === 'asc' ? 'desc' : undefined
                        );
                        setPage(0);
                    }}
                >
                    Wunschtermin
                    {desiredDateSort === 'asc' ? (
                        <ArrowUp className='h-3.5 w-3.5' />
                    ) : desiredDateSort === 'desc' ? (
                        <ArrowDown className='h-3.5 w-3.5' />
                    ) : (
                        <ArrowUpDown className='h-3.5 w-3.5 opacity-50' />
                    )}
                </button>
            ),
            cell: ({ row }) => {
                const { desiredDate, desiredTimeOfDay } = row.original;
                const timeOfDayConfig: Record<string, { label: string; className: string }> = {
                    MORNING: { label: 'Morgens (8-12)', className: 'bg-gray-100 text-gray-500 border-gray-200' },
                    AFTERNOON: { label: 'Nachmittags (12-16)', className: 'bg-gray-200 text-gray-600 border-gray-300' },
                    EVENING: { label: 'Abends (16-20)', className: 'bg-gray-300 text-gray-700 border-gray-400' },
                };
                const tod = desiredTimeOfDay ? timeOfDayConfig[desiredTimeOfDay] : null;
                return (
                    <div>
                        <span className='text-xs'>
                            {desiredDate
                                ? format(new Date(desiredDate), 'dd.MM.yyyy', { locale: de })
                                : '-'}
                        </span>
                        {tod && (
                            <Badge variant='outline' className={cn('text-xs mt-1 block w-fit', tod.className)}>{tod.label}</Badge>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'callStatus',
            header: 'Kontakt-Status',
            cell: ({ row }) => {
                const { callStatus, hasExistingAppointment, existingAppointmentDate } = row.original;
                return (
                    <div>
                        {callStatus === 'CALLED' && (
                            <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>Angerufen</Badge>
                        )}
                        {callStatus === 'NOT_REACHED' && (
                            <Badge variant='default' className='bg-red-100 text-red-800 hover:bg-red-100'>Nicht erreicht</Badge>
                        )}
                        {callStatus === 'CONTACTED' && (
                            <Badge variant='outline' className='bg-green-50 text-green-700 border-green-200'>Anderweitig kontaktiert</Badge>
                        )}
                        {!callStatus && (
                            <span className='text-sm text-muted-foreground'>-</span>
                        )}
                        {hasExistingAppointment && (
                            <div className='text-xs text-red-600 font-medium mt-0.5'>
                                Termin bereits vorhanden
                                {existingAppointmentDate && (
                                    <div className='font-normal'>(am {format(new Date(existingAppointmentDate), 'dd.MM.yyyy', { locale: de })})</div>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'callComment',
            header: 'Kommentar',
            cell: ({ row }) => (
                <span className='text-sm text-muted-foreground max-w-[150px] block truncate'>
                    {row.original.callComment || '-'}
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.status} />,
        },
    ];

    const table = useReactTable({
        data: (data?.data as WaitlistRow[] | undefined) ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount: data?.pageCount ?? 0,
    });

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>Warteliste</h1>
                </div>

                {/* Filters */}
                <Card className='mb-6'>
                    <CardContent className='pt-6'>
                        <div className='flex flex-wrap gap-4'>
                            {/* Search */}
                            <div className='min-w-[250px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Suche</Label>
                                <div className='relative'>
                                    <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Name, E-Mail oder Telefon...'
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className='pl-9'
                                    />
                                </div>
                            </div>
                            <div className='min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Zeitraum</Label>
                                <Popover
                                    open={dateRangeOpen}
                                    onOpenChange={(open) => {
                                        setDateRangeOpen(open);
                                        if (open) {
                                            setIsSelectingToDate(false);
                                        }
                                    }}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant='outline'
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !dateFrom && !dateTo && 'text-muted-foreground'
                                            )}
                                        >
                                            <CalendarIcon className='mr-2 h-4 w-4' />
                                            {dateFrom && dateTo ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(dateTo, 'dd.MM.yyyy', { locale: de })}`
                                            ) : dateFrom ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ...`
                                            ) : (
                                                'Zeitraum wählen'
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-auto p-0' align='start'>
                                        <div className='p-3 border-b h-[52px] flex flex-col justify-center'>
                                            <p className='text-sm text-muted-foreground'>
                                                {isSelectingToDate ? 'Wähle das Enddatum' : 'Wähle das Startdatum'}
                                            </p>
                                            <p className='text-xs text-muted-foreground h-4'>
                                                {dateFrom && isSelectingToDate
                                                    ? `Start: ${format(dateFrom, 'dd. MMM yyyy', { locale: de })}`
                                                    : '\u00A0'
                                                }
                                            </p>
                                        </div>
                                        <Calendar
                                            mode='range'
                                            locale={de}
                                            selected={
                                                isSelectingToDate
                                                    ? (dateFrom ? { from: dateFrom, to: dateFrom } : undefined)
                                                    : undefined
                                            }
                                            onDayClick={(day) => {
                                                if (!isSelectingToDate) {
                                                    setDateFrom(day);
                                                    setDateTo(undefined);
                                                    setIsSelectingToDate(true);
                                                } else {
                                                    let finalFrom = dateFrom;
                                                    let finalTo = day;

                                                    if (dateFrom && day < dateFrom) {
                                                        finalFrom = day;
                                                        finalTo = dateFrom;
                                                    }

                                                    setDateFrom(finalFrom);
                                                    setDateTo(finalTo);
                                                    setIsSelectingToDate(false);
                                                    setDateRangeOpen(false);
                                                    setPage(0);
                                                }
                                            }}
                                            numberOfMonths={2}
                                            defaultMonth={dateFrom || new Date()}
                                        />
                                        {(dateFrom || dateTo) && (
                                            <div className='p-2 border-t flex justify-end'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() => {
                                                        setDateFrom(undefined);
                                                        setDateTo(undefined);
                                                        setIsSelectingToDate(false);
                                                        setDateRangeOpen(false);
                                                        setPage(0);
                                                    }}
                                                >
                                                    Zurücksetzen
                                                </Button>
                                            </div>
                                        )}
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className='min-w-[180px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Status</Label>
                                <Select
                                    value={statusFilter}
                                    onValueChange={(value: 'all' | WaitlistStatus) => {
                                        setStatusFilter(value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Alle Status' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>Alle Status</SelectItem>
                                        <SelectItem value='booked'>Gebucht</SelectItem>
                                        <SelectItem value='deleted'>Gelöscht</SelectItem>
                                        <SelectItem value='expired'>Abgelaufen</SelectItem>
                                        <SelectItem value='notified'>Benachrichtigt</SelectItem>
                                        <SelectItem value='open'>Offen</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Kontakt-Status</Label>
                                <Select
                                    value={callStatusFilter}
                                    onValueChange={(value: 'all' | WaitlistCallStatus | 'NONE') => {
                                        setCallStatusFilter(value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Alle' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>Alle</SelectItem>
                                        <SelectItem value='CALLED'>Angerufen</SelectItem>
                                        <SelectItem value='NOT_REACHED'>Nicht erreicht</SelectItem>
                                        <SelectItem value='CONTACTED'>Anderweitig kontaktiert</SelectItem>
                                        <SelectItem value='NONE'>Kein Status</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='min-w-[180px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Filiale</Label>
                                <Select
                                    value={storeFilter}
                                    onValueChange={(value) => {
                                        setStoreFilter(value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Alle Filialen' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                                        {stores.map((store) => (
                                            <SelectItem key={store.id} value={store.id.toString()}>
                                                {store.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card>
                    <div className='rounded-md border'>
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {columns.map((_, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton className='h-4 w-full' />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : table.getRowModel().rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                            Keine Wartelisten-Einträge gefunden
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            className='cursor-pointer hover:bg-muted/50'
                                            onClick={() => handleRowClick(row.original)}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {cell.column.id === 'customer' ? (
                                                        <div className='relative z-[2]'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </div>
                                                    ) : (
                                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Card>

                {/* Pagination */}
                {data && data.pageCount > 1 && (
                    <div className='flex items-center justify-between mt-4'>
                        <div className='text-sm text-muted-foreground'>
                            Seite {page + 1} von {data.pageCount} ({data.totalCount} Einträge)
                        </div>
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.max(0, page - 1))}
                                disabled={page === 0}
                            >
                                Zurück
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.min(data.pageCount - 1, page + 1))}
                                disabled={page >= data.pageCount - 1}
                            >
                                Weiter
                            </Button>
                        </div>
                    </div>
                )}

                {/* Detail / Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className='sm:max-w-[500px]'>
                        <DialogHeader>
                            <DialogTitle>Wartelisten-Eintrag</DialogTitle>
                        </DialogHeader>
                        {selectedRow && (
                            <div className='space-y-4'>
                                <div>
                                    <Label className='text-sm font-medium'>Kunde</Label>
                                    <p className='text-sm mt-1'>
                                        {selectedRow.customerName ?? selectedRow.email}
                                        {selectedRow.customerName && (
                                            <span className='text-muted-foreground ml-2'>({selectedRow.email})</span>
                                        )}
                                    </p>
                                </div>

                                <div>
                                    <Label className='text-sm font-medium'>Leistungen</Label>
                                    <p className='text-sm mt-1'>{selectedRow.bookableItemNames || '-'}</p>
                                </div>

                                {selectedRow.hasExistingAppointment && (
                                    <div className='rounded-md bg-red-50 border border-red-200 p-3'>
                                        <p className='text-sm text-red-700 font-medium'>
                                            Termin bereits vorhanden
                                        </p>
                                        <p className='text-xs text-red-600 mt-0.5'>
                                            Der Kunde hat bereits einen Termin für diese Leistungen gebucht.
                                            {selectedRow.existingAppointmentDate && (
                                                <span> (am {format(new Date(selectedRow.existingAppointmentDate), 'dd.MM.yyyy', { locale: de })})</span>
                                            )}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <Label className='text-sm font-medium'>Kontakt-Status</Label>
                                    <Select
                                        value={editCallStatus}
                                        onValueChange={(value: WaitlistCallStatus | 'NONE') => setEditCallStatus(value)}
                                    >
                                        <SelectTrigger className='mt-1'>
                                            <SelectValue placeholder='Status wählen' />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='NONE'>Kein Status</SelectItem>
                                            <SelectItem value='CALLED'>Angerufen</SelectItem>
                                            <SelectItem value='NOT_REACHED'>Nicht erreicht</SelectItem>
                                            <SelectItem value='CONTACTED'>Anderweitig kontaktiert</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label className='text-sm font-medium'>Kommentar</Label>
                                    <Textarea
                                        className='mt-1'
                                        placeholder='Kommentar hinzufügen...'
                                        value={editCallComment}
                                        onChange={(e) => setEditCallComment(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant='outline' onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={updateCallStatusMutation.isPending}
                            >
                                {updateCallStatusMutation.isPending ? 'Speichern...' : 'Speichern'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Content>
    );
};

export default WaitlistSubmissions;
